import { Alert, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';

import { useCurrentUserQuery } from 'entities/user';
import { calendarDaysUntilDate } from 'shared/lib/date';

const ACCOUNT_EXPIRY_WEEK_WARNING_DISMISSED_KEY = 'executiveiq.accountExpiryWeekWarningDismissed';
const EXPIRY_WARNING_DAYS = 7;

function readExpiryWeekWarningDismissed(): { accountId: number; activePeriodEndsAt: string } | null {
  try {
    const raw = localStorage.getItem(ACCOUNT_EXPIRY_WEEK_WARNING_DISMISSED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { accountId?: number; activePeriodEndsAt?: string };
    if (typeof parsed.accountId !== 'number' || typeof parsed.activePeriodEndsAt !== 'string') return null;
    return { accountId: parsed.accountId, activePeriodEndsAt: parsed.activePeriodEndsAt };
  } catch {
    return null;
  }
}

function isExpiryWeekWarningDismissed(accountId: number, endsAt: string): boolean {
  const d = readExpiryWeekWarningDismissed();
  return d?.accountId === accountId && d?.activePeriodEndsAt === endsAt;
}

function shouldShowAccountExpiryWeekWarning(endsOn?: string): boolean {
  if (!endsOn) return false;
  const daysLeft = calendarDaysUntilDate(endsOn);
  if (daysLeft === null) return false;
  return daysLeft >= 0 && daysLeft <= EXPIRY_WARNING_DAYS;
}

const bannerOuterSx = {
  width: '100%',
  boxSizing: 'border-box',
} as const;

const AccountExpiryWeekWarningBanner = () => {
  const { data: currentUser } = useCurrentUserQuery();

  const accountId = currentUser?.account?.id;
  const accountEndsOn = currentUser?.account?.activePeriodEndsAt;

  const [expiryBannerHidden, setExpiryBannerHidden] = useState(false);

  useEffect(() => {
    setExpiryBannerHidden(false);
  }, [accountId, accountEndsOn]);

  const isDismissedInStorage = useMemo(
    () => accountId != null && accountEndsOn != null && isExpiryWeekWarningDismissed(accountId, accountEndsOn),
    [accountId, accountEndsOn],
  );

  const showExpiryWeekBanner =
    Boolean(accountEndsOn) &&
    shouldShowAccountExpiryWeekWarning(accountEndsOn) &&
    !isDismissedInStorage &&
    !expiryBannerHidden;

  const formattedExpiration = accountEndsOn
    ? new Date(`${accountEndsOn}T12:00:00`).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : '';

  const handleExpiryBannerClose = () => {
    if (accountId != null && accountEndsOn) {
      localStorage.setItem(
        ACCOUNT_EXPIRY_WEEK_WARNING_DISMISSED_KEY,
        JSON.stringify({ accountId, activePeriodEndsAt: accountEndsOn }),
      );
      setExpiryBannerHidden(true);
    }
  };

  if (!showExpiryWeekBanner) {
    return null;
  }

  return (
    <Box sx={bannerOuterSx} role="region" aria-label="Account expiry notice">
      <Alert
        severity="warning"
        variant="outlined"
        onClose={handleExpiryBannerClose}
        sx={(theme) => ({
          width: '100%',
          maxWidth: '100%',
          borderRadius: 0,
          border: 'none',
          borderTop: `1px solid ${theme.palette.warning.main}`,
          borderBottom: `1px solid ${theme.palette.warning.main}`,
          boxSizing: 'border-box',
          bgcolor: alpha(theme.palette.warning.main, 0.12),
          '& .MuiAlert-message': {
            width: '100%',
          },
        })}
      >
        {`Your account access expires on ${formattedExpiration}.`}
      </Alert>
    </Box>
  );
};

export { AccountExpiryWeekWarningBanner };
